/**
 * Here you can export functions with corresponding services / endpoints.
 * You can always use the services directly from the specific moduleServices file as well.
 * This is to keep a track of all available services as a list.
 * I think it makes is easy to maintain when you have a long long list as your app gradually scales.
 */
import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes } from '../authConfig';
import { msalInstance } from './msal';
import { decryptJson, encryptText } from '@utils/openpgp';
import { getRedirectFn } from '../RedirectManager';
import { notifyGlobalError } from '@components/GlobalNotificationManager';

export { getAllTempUsers, getTempUserDetails, addTempUser } from './tempUserServices';
export {
    addUser,
    searchUsers,
    getUserDetails,
    editUser,
    getRolesOptions,
    searchActionedUsers,
    searchPendingUsers,
    withdrawUser,
    approveRejectUser,
    awaitingDeactivateUser,
    getUserInfo,
    getPortfoliosTypeOptions,
    getDelegatedModules
} from './userServices';

export {
    getRefFileTypes,
    addRefFiletype,
    editRefFiletype,
    getRefFileDetails,
    searchRefdata,
    deleteRefFileDetails,
    refFileTypeStatusUpdate,
    getRefFileTypeDetails,
    uploadRefFile,
    getRefFileTypesOptions,
    getRefTypeChannelsById,
    getAllRefTypeChannelsById,
    updateRefData,
    getRequestHistory,
    requesterUpdateRefData,
    getRefFileUrl,
    searchReffileBulk,
    getBulkRefFileDetails,
    withdrawBulkRefFile,
    getBulkRefFileUrl,
    updateBulkReffile,
    bulkUploadRefFile,
    getRefFileTypesPortfolioMatch,
    searchPendingRefData,
    searchActionedRefData,
    searchPendingReffileBulk,
    searchActionedReffileBulk
} from './refdataServices';

export {
    createDropdown,
    deletDropdown,
    updateDropdownValues,
    getFormOptions,
    getFormFields,
    updateFormFields
} from './formCustomizationServices';

export {
    searchPayee,
    getPayeeDetails,
    editPayee,
    getPayeeFormData,
    addPayee,
    updatePayeeData,
    withdrawPayee,
    payeeBulkFileUpload,
    searchPayeeBulk,
    getBulkPayeeDetails,
    withdrawBulkPayee,
    updateBulkPayee,
    searchParentPayee,
    searchPaymentReports,
    getPayeeheirarchy,
    getAllPayees,
    getBatchSupportingFilesList,
    updatePaymentStatus,
    searchPendingPayeeBulk,
    searchActionedPayeeBulk,
    getBulkPayeeJobStatus,
    sendEmailNotification,
    searchPayeeBulkDeactivate,
    getPayeeBulkDeactivateDetails,
    getPendingPayeeBulkDeactivate,
    getActionedPayeeBulkDeactivate,
    approvePayeeBulkDeactivate
} from './payeeServices';

export {
    searchRawDataBatch,
    searchPendingRawData,
    searchActionedRawData,
    getRawDataBatchDetails,
    getRawDataTables,
    updateRawDataBatch,
    getRawDataFields,
    updateRawDataFields,
    rawDataBatchStatusUpdate,
    withdrawRawData,
    approveRejectRawDataBatch
} from './rawDataServices';

export {
    addChannel,
    editChannel,
    getChannelDetails,
    searchChannels,
    searchSubChannels,
    getSubChannelDetails,
    getBusinessUnitOptions,
    getPrimaryChannelOptions,
    addSubChannel,
    editsubChannel,
    getPortfolioOptions,
    subChannelStatusUpdate,
    channelStatusUpdate
} from './channelServices';

export {
    searchWorkflows,
    createDagRun,
    checkStatus,
    getWorkflowRequestHistory,
    updateWorkflowAssignment,
    getCAList,
    getSupportingFiles,
    searchLatestBatchDagStatus,
    stopDagRun,
    updateWorkflowStatus,
    getCAWorkflowList,
    updateCAWorkflowAssignment,
    getFiscalYearConfig,
    updateFiscalYearConfig
} from './workflowServices';

export {
    getCalculationBatchDetails,
    updateCalculationBatch,
    searchCalculationDataBatch,
    aproveRejectCalculation,
    releaseRetractUpdate,
    bulkUpdateCalculationBatch,
    searchActionedCalculationBatch,
    searchPendingCalculationBatch
} from './calculationServices';

export {
    downloadFile,
    uploadFileToBucket,
    uploadBulkFilesToBucket,
    getDashboardCount,
    fileToStream
} from './commonServices';

export {
    getTransFileTypes,
    addTransFiletype,
    getTransFileTypeDetails,
    transFileTypeStatusUpdate,
    editTransFiletype,
    deleteTransFileType,
    searchTransData,
    getTransFileDetails,
    updateTransData,
    getTransFileTypesOptions,
    uploadTransFile,
    bulkUploadTransFile,
    getTransFileTypeChannelsById,
    getAllTransTypeChannelsById,
    requesterUpdateTransData,
    getTransFileUrl,
    searchTransfileBulk,
    getBulkTransFileDetails,
    getBulkTransFileUrl,
    updateBulkTransfile,
    withdrawBulkTransFile,
    getTransFileTypesPortfolioMatch,
    searchActionedTransData,
    searchPendingTransData,
    searchActionedTransfileBulk,
    searchPendingTransfileBulk
} from './transDataServices';

export {
    searchPayeeClaims,
    searchCAClaims,
    getClaimDetails,
    searchManagerClaims,
    claimAssignmentToCA,
    searchCAPendingClaims,
    searchCAActionsClaims,
    getRequestHistoryClaims,
    getRequestHistoryClaimsPayee,
    updateCAApproval,
    searchManagerPendingClaims,
    searchManagerActionsClaims,
    getCAListPortfolioMatch,
    addEditCommissionType,
    searchScheme,
    getCommissionTypeDetails,
    deleteCommissionType,
    getAllCA,
    checkLinkedCommissionType,
    getCommissionTypesOptions,
    enableWorking,
    addEditClaim,
    withdrawClaim,
    addRejectionReason,
    searchRejections,
    updateRejectionReason,
    getRejectionReasonOptions,
    deleteClaim,
    addAssignCommissionTypes
} from './claimServices';

export {
    getSchemeDocuments,
    getSchemeDocumentDetails,
    deleteSchemeDocument,
    isSchemeDocumentLinked,
    uploadSchemeDocument,
    getAllSchemeDocuments,
    addEditBatchTarget,
    searchBatchTargets,
    getBatchTargetDetails,
    updateBatchTargetStatus,
    updatePayeeTargetStatus,
    getPayeeTargets,
    getPayeeTargetDetails,
    searchPayeeTargets,
    getTargetRequestHistory,
    searchActionedBatchTargets,
    searchPendingBatchTargets,
    getYearlyKpis,
    addEditYearlyKpi,
    getYearKpiDetails,
    deleteYearlyKpi
} from './payeeTargetServices';

export {
    getModules, getAllSA, addDelegation, searchDelegations, getDelegatesDetails, inactiveDelegatee
} from './delegationServices';

export {
    searchAuditorEmails,
    addAuditorEmail,
    deleteAuditorEmail
} from './auditorEmailServices';

export {
    getClaimsSubmittedOverTime,
    getClaimsByStatus,
    getClaimsByCommissionType
} from './dashboardClaimsServices';

export {
    getTargetReminderConfig,
    updateTargetReminderConfig
} from './targetReminderServices';

export {
    getEmailModules,
    getActionsByModule,
    getEmailTemplate,
    updateEmailTemplate
} from './emailConfigurationServices';

axios.interceptors.request.use(
    async config => {
        try {
            const account = msalInstance.getAllAccounts()[0];
            const silentRequest = { ...apiScopes, account };
            
            config.headers.opco = localStorage.getItem('opco');

            // Check localStorage values before encryption
            const sToken = localStorage.getItem('sToken');
            const userId = localStorage.getItem('userId');

            // Skip token generation for login requests (sToken/userId not available yet)
            const isLoginRequest = config.url && config.url.includes('/login');

            if (!isLoginRequest) {
                if (!sToken || !userId) {
                    console.error('[AXIOS-INTERCEPTOR] Missing sToken or userId for non-login request');
                }
                // Generate per-request token
                const nonce = generateNonce();
                const timestamp = Date.now();
                const tokenPayload = JSON.stringify({ nonce, timestamp });
                const encrypted = await encryptText(tokenPayload);
                config.headers['X-Request-Token'] = btoa(encrypted);
            }

            try {
                if (account) {
                    const accessTokenResponse = await msalInstance.acquireTokenSilent(silentRequest);
                    if (accessTokenResponse) {
                        const { accessToken } = accessTokenResponse;
                        if (config.headers && accessToken) {
                            config.headers.Authorization = `Bearer ${accessToken}`;
                        }
                    }
                }
                return config;
            } catch (err) {
                console.error('[AXIOS-INTERCEPTOR] Token acquisition error:', err.message);
                if (err instanceof InteractionRequiredAuthError) {
                    try {
                        const accessTokenResponse = await msalInstance.acquireTokenPopup(silentRequest);
                        if (accessTokenResponse) {
                            const { accessToken } = accessTokenResponse;
                            if (config.headers && accessToken) {
                                config.headers.Authorization = `Bearer ${accessToken}`;
                            }
                        }
                        return config;
                    } catch (popupError) {
                        console.error('[AXIOS-INTERCEPTOR] Popup failed:', popupError.message);
                        if (popupError instanceof InteractionRequiredAuthError) {
                            return msalInstance.acquireTokenRedirect(silentRequest);
                        }
                        throw popupError;
                    }
                } else {
                    throw err;
                }
            }
        } catch (error) {
            console.error('[AXIOS-INTERCEPTOR] FATAL:', error.message, error.stack);
            throw error;
        }
    },
    async error => {
        console.error('[AXIOS-INTERCEPTOR] Request error:', error.message);
        return Promise.reject(error);
    }
);

axios.interceptors.response.use(
    async response => {
        if (localStorage.getItem('sToken') && localStorage.getItem('userId')) {
            if (response.data instanceof Blob) return response;

            const decrypted = await decryptJson(response.data);

            let parsed = decrypted;

            // handle encrypted string JSON
            if (typeof decrypted === "string") {
                try {
                    parsed = JSON.parse(decrypted);
                } catch {
                    parsed = decrypted;
                }
            }

            if (parsed && typeof parsed === "object" && "data" in parsed && Object.keys(parsed).length === 1) {
                response.data = parsed.data;
            } else {
                response.data = parsed;
            }
        }

        return response;
    },
    async error => {
        const status = error?.response?.status;
        const code = error?.response?.data?.code;
        const redirect = getRedirectFn();

        /* ---------------- Redirect handling ---------------- */

        if (redirect) {
            if (status === 403) redirect('/access-denied');
            else if (status === 404 && code === 'ROUTE_NOT_FOUND') redirect('/not-found');
        }

        /* ---------------- Decrypt error response ---------------- */

        if (localStorage.getItem('sToken') && localStorage.getItem('userId') && error.response?.data && !(error.response.data instanceof Blob)) {
            try {
                const decryptedJson = await decryptJson(error.response.data);

                if (typeof decryptedJson === "string") {
                    try {
                        error.response.data = JSON.parse(decryptedJson);
                    } catch {
                        error.response.data = decryptedJson;
                    }
                } else {
                    error.response.data = decryptedJson;
                }
            } catch {
                // ignore decrypt failure
            }
        }

        /* ---------------- Global toaster logic ---------------- */

        const isValidationError = status === 400;
        const isNetworkError = !error?.response;

        if (!isValidationError && !isNetworkError) {
            const errorMessage =
                error?.response?.data?.message ||
                'Something went wrong. Please try again later.';

            notifyGlobalError(errorMessage);
        }

        return Promise.reject(error);
    }
);

const generateNonce = () => {
    if (crypto.randomUUID) {
        return crypto.randomUUID();
    }

    const array = new Uint8Array(16);
    crypto.getRandomValues(array); // check it is working
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
};